import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { cn } from '../../../lib/cn';
import { ArrowLeftGlyph } from './icons';
import { useReducedMotion } from './useReducedMotion';
import { useSheetDrag } from './useSheetDrag';
import '../../../patterns/motion.css';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableIn(node) {
  if (!node) return [];
  return Array.from(node.querySelectorAll(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

/* Nested sheets are legal (a filter sheet opening a confirm sheet), so the
   body scroll lock is refcounted. A naive lock releases on the first close and
   lets the page behind scroll while the outer sheet is still up. */
let scrollLocks = 0;
let previousOverflow = '';

function lockBodyScroll() {
  if (typeof document === 'undefined') return;
  if (scrollLocks === 0) {
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLocks += 1;
}

function unlockBodyScroll() {
  if (typeof document === 'undefined') return;
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks === 0) document.body.style.overflow = previousOverflow;
}

/* The sheet owns the id, the header owns the element that carries it. Context
   is the only way to wire aria-labelledby across that gap without forcing every
   call site to invent and thread an id by hand. */
const SheetTitleContext = createContext(null);

/* The drag handle props, carried across the same gap and for the same reason as
   the title id above. The grabber belongs to `Sheet` and the header belongs to
   `SheetHeader`, which every call site renders as a CHILD -- so the only way to
   make both halves of one gesture without touching thirteen call sites is to
   hand the handlers down. Null when the sheet cannot be dragged (no `onClose`,
   or an exit already in flight), which is also what makes `SheetHeader` usable
   outside a `Sheet` without it reaching for a gesture that is not there. */
const SheetDragContext = createContext(null);

/* The unmount wait. Must stay identical to the desktop Drawer's `DRAWER_MS`,
   because a surface that is a Drawer above 1024 and a Sheet below it must not
   change tempo when the viewport crosses the handoff -- and below the handoff
   `Drawer` mounts THIS component, so the two numbers govern the same close.

   No longer tied to --ds-transition-base: the exit is a keyframe animation in
   `patterns/motion.css`, whose `--dsa-motion-exit` went 180ms -> 280ms to stop
   the sheet snapping down. 320ms keeps the wait 40ms clear of it; equal numbers
   let one slow frame tear the node out mid-slide, which reads as the sheet
   vanishing rather than leaving. */
const SHEET_MS = 320;

/**
 * Scrim.
 *
 * CSS selector: `organisms.css .ds-scrim` and `.ds-scrim--heavy`.
 *
 * Doctrine: the scrim and the sheet are the only floating layers in the mobile
 * track, and the only two things allowed to cast a shadow. `--heavy` is for
 * save moments, where the sheet is asking for a decision rather than offering
 * a choice. Exported separately from `Sheet` so a template can dim without a
 * sheet, but the usual path is to let `Sheet` render it.
 */
export const Scrim = forwardRef(function Scrim({ className, heavy = false, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={cn('ds-scrim', heavy && 'ds-scrim--heavy', className)}
      {...rest}
    />
  );
});

/**
 * Sheet, the mobile workhorse.
 *
 * CSS selector: `organisms.css .ds-sheet`, with `.ds-scrim` behind it.
 *
 * Doctrine and behaviour, all required:
 *
 * - `role="dialog"` + `aria-modal="true"`, and an accessible name from either
 *   `aria-label` or the `SheetHeader` title via `aria-labelledby`.
 * - Focus moves into the sheet on open, is TRAPPED inside it while open, and is
 *   restored to whatever was focused before on close.
 * - Escape closes. A scrim click closes. Both route through the same `onClose`.
 * - Body scroll is locked while open, refcounted so nested sheets behave.
 * - `prefers-reduced-motion: reduce` switches the drill slide off entirely
 *   rather than merely shortening it.
 * - It SLIDES UP on open and SLIDES BACK DOWN on close, scrim fading with it.
 *   CSS for both lives in `patterns/motion.css`.
 * - It DRAGS DOWN TO DISMISS, from the grabber or the header and never from the
 *   body -- see `useSheetDrag` for why the handle stops there. A release past a
 *   quarter of the sheet's height, or a downward flick, routes through the same
 *   `onClose` as every other dismissal (with the reason `'drag'`); anything
 *   shorter springs back. Reduced motion keeps the gesture and drops the spring.
 * - Radius is 24px and comes from `--ds-radius-sheet` inside `.ds-sheet`. Never
 *   restate it at a call site.
 *
 * Positioning: `.ds-sheet` and `.ds-scrim` are `position: absolute`, so the
 * nearest positioned ancestor is the sheet's viewport. On a converted page that
 * is the app frame. This is deliberate, it keeps the sheet inside the phone
 * frame in the design harness and inside the app shell in production, with no
 * portal and no z-index arms race.
 *
 * THE EXIT ANIMATION NEEDS `open`, NOT AN UNMOUNT. A call site that writes
 * `{isOpen && <Sheet .../>}` tears the node out on the same tick it closes, so
 * there is nothing left to animate and the sheet still vanishes. Pass `open`
 * and leave the Sheet mounted; it holds itself in the DOM for one exit and
 * then removes itself.
 */
export const Sheet = forwardRef(function Sheet(
  {
    className,
    open = true,
    onClose,
    scrim = true,
    scrimHeavy = false,
    scrimClassName,
    closeOnScrimClick = true,
    closeOnEscape = true,
    lockScroll = true,
    restoreFocus = true,
    initialFocusRef,
    grabber = true,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    style,
    children,
    ...rest
  },
  ref,
) {
  const sheetRef = useRef(null);
  const returnFocusRef = useRef(null);
  const generatedId = useId();
  const titleId = ariaLabelledBy || `${generatedId}-sheet-title`;
  const reduced = useReducedMotion();

  /* Two flags, not one, and the same pair the desktop Drawer keeps. `mounted`
     holds the node in the DOM past `open` going false so the slide-down has
     something to play on; `exiting` is the class that plays it. Before this the
     Sheet returned null the instant it closed, which is half an animation. */
  const [mounted, setMounted] = useState(Boolean(open));
  const [exiting, setExiting] = useState(false);

  /* Opening is adjusted DURING RENDER, not from an effect. Do it in an effect
     and `mounted` catches up one render late, which means the effect below that
     moves focus into the sheet runs while `sheetRef.current` is still null and
     the sheet opens with focus stranded on the page behind it. React re-runs
     this render immediately and nothing in between is ever committed. */
  if (open && (!mounted || exiting)) {
    setMounted(true);
    setExiting(false);
  }

  useEffect(() => {
    /* Guard on `mounted`: without it a Sheet that renders closed from the start
       would schedule an exit it never entered. */
    if (open || !mounted) return undefined;

    setExiting(true);
    /* Reduced motion collapses the wait to zero rather than shortening it. The
       CSS drops the animation entirely there, so any wait at all would just be
       a pause on a motionless panel. */
    const timer = setTimeout(() => {
      setExiting(false);
      setMounted(false);
    }, reduced ? 0 : SHEET_MS);
    return () => clearTimeout(timer);
  }, [open, mounted, reduced]);

  const assign = useCallback(
    (node) => {
      sheetRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  /* The lock is keyed on `mounted`, NOT on `open`. `open` goes false at the
     START of the exit, so unlocking there handed the page behind back its
     scroll while the sheet was still fully opaque and sliding down -- on a phone
     the content visibly moved under a modal surface. `mounted` only goes false
     when the node actually leaves, which is the first moment there is nothing
     left to be modal about. */
  useEffect(() => {
    if (!mounted || !lockScroll) return undefined;
    lockBodyScroll();
    return unlockBodyScroll;
  }, [mounted, lockScroll]);

  /* Focus, unlike the lock, does stay on `open`: the panel is `aria-hidden`
     for the length of the exit (see the render below), and focus parked inside
     an aria-hidden subtree is read by nothing. It goes back to the trigger the
     moment the sheet is dismissed. */
  useEffect(() => {
    if (!open) return undefined;

    returnFocusRef.current = typeof document !== 'undefined' ? document.activeElement : null;

    const target =
      (initialFocusRef && initialFocusRef.current) ||
      focusableIn(sheetRef.current)[0] ||
      sheetRef.current;
    if (target && target.focus) target.focus({ preventScroll: true });

    return () => {
      const back = returnFocusRef.current;
      if (restoreFocus && back && back.focus) back.focus({ preventScroll: true });
    };
  }, [open, restoreFocus, initialFocusRef]);

  useEffect(() => {
    if (!open) return undefined;

    function onKeyDown(event) {
      if (event.key === 'Escape' && closeOnEscape) {
        event.stopPropagation();
        if (onClose) onClose('escape');
        return;
      }
      if (event.key !== 'Tab') return;

      const items = focusableIn(sheetRef.current);
      if (items.length === 0) {
        event.preventDefault();
        if (sheetRef.current) sheetRef.current.focus({ preventScroll: true });
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !sheetRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, closeOnEscape, onClose]);

  /* `enabled` is `!exiting`, not `open`. It has to stay true across the tick
     where a drag CAUSES the close -- `onClose('drag')` flips `open` false, and
     a hook disabled on that same commit would clear the offset it is supposed
     to hand to the exit animation. `exiting` only goes true one effect later,
     by which time the offset has already been read. */
  const drag = useSheetDrag({ panelRef: sheetRef, onClose, enabled: !exiting });

  /* `mounted`, not `open`: the two differ only for the length of one exit, and
     that is the whole point -- rendering on `open` is what made the exit
     unplayable. */
  if (!mounted) return null;

  return (
    <>
      {scrim && (
        <Scrim
          heavy={scrimHeavy}
          /* Only the --in/--out modifiers, no bare `dsa-motion-scrim`. That
             base class exists to neutralise a hidden resting state, and the
             desktop `.dsk-drawer-scrim` is the only surface that has one;
             `.ds-scrim` already rests opaque. On this track it matched no rule
             at all, which is a class that lies about what it does. */
          className={cn(
            exiting ? 'dsa-motion-scrim--out' : 'dsa-motion-scrim--in',
            scrimClassName,
          )}
          /* Thins with the drag. Inline rather than a class because the value
             is continuous -- it tracks the thumb, not a state. */
          style={drag.scrimStyle}
          onClick={!exiting && closeOnScrimClick && onClose ? () => onClose('scrim') : undefined}
        />
      )}
      <div
        ref={assign}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabel ? undefined : titleId}
        /* A sheet on its way out is no longer a dialog anyone is in: focus has
           already gone back to the trigger, so leaving it exposed would put a
           second, dead dialog in the a11y tree for the length of the exit. */
        aria-hidden={exiting || undefined}
        tabIndex={-1}
        /* No bare `dsa-motion-panel` here either, for the reason on the scrim:
           `.ds-sheet` rests at its final visible position, so there is nothing
           for a base class to undo. */
        className={cn(
          'ds-sheet',
          exiting ? 'dsa-motion-panel--out' : 'dsa-motion-panel--in',
          /* `--drag` kills the entry keyframe: an animation and an inline
             transform are two authors writing the same property, and the
             animation wins, so without this the panel ignores the thumb for
             the first 340ms of its life. `--settle` is the transition that
             carries it back on a released drag that did not close it. */
          drag.dragging && 'dsa-motion-panel--drag',
          drag.settling && 'dsa-motion-panel--settle',
          className,
        )}
        style={drag.panelStyle ? { ...style, ...drag.panelStyle } : style}
        {...rest}
      >
        {grabber && (
          <span
            /* Spread FIRST, then re-state className from the merge: the handle
               props carry a class of their own and a bare spread would drop
               `.ds-sheet__grabber` on the floor, taking the pill's whole
               appearance with it. */
            {...drag.handleProps}
            className={cn('ds-sheet__grabber', drag.handleProps && drag.handleProps.className)}
            aria-hidden="true"
          />
        )}
        <SheetTitleContext.Provider value={titleId}>
          <SheetDragContext.Provider value={drag.handleProps}>{children}</SheetDragContext.Provider>
        </SheetTitleContext.Provider>
      </div>
    </>
  );
});

/**
 * SheetHeader.
 *
 * CSS selector: `organisms.css .ds-sheet__header`, `--drill`,
 * `.ds-sheet__title`, `--lg`, `.ds-sheet__subtitle`. The applied count reuses
 * `.ds-filterbtn__count`.
 *
 * Doctrine: `drill` is the sub panel header, back button plus title, left
 * aligned and with no trailing slot. `appliedCount` is the indicator that comes
 * back with you from a drill in, so a user who applied four rarities sees the
 * four without reopening the panel. The count pill is INK, not accent, because
 * it reports a command the user issued.
 */
export const SheetHeader = forwardRef(function SheetHeader(
  {
    className,
    title,
    subtitle,
    titleSize = 'base',
    trailing,
    drill = false,
    onBack,
    backLabel = 'Back',
    appliedCount,
    children,
    ...rest
  },
  ref,
) {
  const titleId = useContext(SheetTitleContext);
  /* The second half of the drag handle. A grabber is 40x4px and the header is
     the whole width of the sheet, which is what makes the gesture findable at
     all -- a thumb aiming for a 4px pill misses it. Null outside a Sheet, and
     null on a Sheet that cannot be dragged, so this component stays usable on
     its own. */
  const dragProps = useContext(SheetDragContext);
  const titleNode = title && (
    <h2
      id={titleId || undefined}
      className={cn('ds-sheet__title', titleSize === 'lg' && 'ds-sheet__title--lg')}
    >
      {title}
    </h2>
  );

  const countNode =
    appliedCount !== undefined && appliedCount !== null && appliedCount > 0 ? (
      <span className="ds-filterbtn__count">{appliedCount}</span>
    ) : null;

  if (drill) {
    return (
      <div
        ref={ref}
        {...dragProps}
        className={cn(
          'ds-sheet__header',
          'ds-sheet__header--drill',
          dragProps && dragProps.className,
          className,
        )}
        {...rest}
      >
        <button type="button" className="ds-backbtn ds-backbtn--bare" aria-label={backLabel} onClick={onBack}>
          <ArrowLeftGlyph />
        </button>
        {titleNode}
        {countNode}
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      {...dragProps}
      className={cn('ds-sheet__header', dragProps && dragProps.className, className)}
      {...rest}
    >
      <div>
        {titleNode}
        {subtitle && <p className="ds-sheet__subtitle">{subtitle}</p>}
      </div>
      {countNode}
      {trailing}
      {children}
    </div>
  );
});

/**
 * SheetBody.
 *
 * CSS selector: `organisms.css .ds-sheet__body`.
 *
 * Doctrine: the body is the only scrolling region. The grabber, header and
 * footer are `flex: none` so the CTAs never scroll away from the thumb.
 */
export const SheetBody = forwardRef(function SheetBody({ className, children, ...rest }, ref) {
  return (
    <div ref={ref} className={cn('ds-sheet__body', className)} {...rest}>
      {children}
    </div>
  );
});

/**
 * SheetFooter.
 *
 * CSS selector: `organisms.css .ds-sheet__footer` and `--stacked`.
 *
 * Doctrine: on filter sheets the CTAs sit SIDE BY SIDE, which is the default
 * here, and the solid sibling leads at `flex: 1.4`. `stacked` is the deliberate
 * exception for form sheets only. Neither CTA is ever accent filled, ink is
 * command.
 */
export const SheetFooter = forwardRef(function SheetFooter(
  { className, stacked = false, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn('ds-sheet__footer', stacked && 'ds-sheet__footer--stacked', className)}
      {...rest}
    >
      {children}
    </div>
  );
});

/**
 * SheetRow.
 *
 * CSS selector: `organisms.css .ds-sheet__row`.
 *
 * Doctrine: the two up field row inside sheet forms. Its children are `.ds-field`
 * molecules, which the CSS flexes to equal width.
 */
export const SheetRow = forwardRef(function SheetRow({ className, children, ...rest }, ref) {
  return (
    <div ref={ref} className={cn('ds-sheet__row', className)} {...rest}>
      {children}
    </div>
  );
});

/**
 * SheetDrill and SheetDrillPanel.
 *
 * CSS selector: no canonical block. Composition of `.ds-sheet__body` children
 * with the panel modifiers `.ds-sheet__panel`, `--enter` and `--exit`.
 *
 * Doctrine: a drill in slides right to left and the return slides back, so the
 * user keeps a spatial model of where the sub panel came from. Only the active
 * panel is mounted once the transition settles, which keeps focus order honest,
 * a hidden second panel would otherwise put unreachable tab stops in the trap.
 * Under `prefers-reduced-motion: reduce` there is no transition at all and the
 * swap is instant.
 */
export const SheetDrill = forwardRef(function SheetDrill(
  { className, index = 0, duration = 200, children, ...rest },
  ref,
) {
  const panels = Array.isArray(children) ? children : [children];
  const reduced = useReducedMotion();
  const [previous, setPrevious] = useState(null);
  const lastIndex = useRef(index);

  useEffect(() => {
    if (index === lastIndex.current) return undefined;
    const from = lastIndex.current;
    lastIndex.current = index;
    if (reduced) {
      setPrevious(null);
      return undefined;
    }
    setPrevious({ index: from, forward: index > from });
    const timer = setTimeout(() => setPrevious(null), duration);
    return () => clearTimeout(timer);
  }, [index, reduced, duration]);

  const forward = previous ? previous.forward : true;

  return (
    <div ref={ref} className={cn('ds-sheet__drill', className)} {...rest}>
      {previous && (
        <div
          className={cn(
            'ds-sheet__panel',
            forward ? 'ds-sheet__panel--exit-back' : 'ds-sheet__panel--exit-forward',
          )}
          aria-hidden="true"
        >
          {panels[previous.index]}
        </div>
      )}
      <div
        className={cn(
          'ds-sheet__panel',
          previous && (forward ? 'ds-sheet__panel--enter-forward' : 'ds-sheet__panel--enter-back'),
        )}
      >
        {panels[index]}
      </div>
    </div>
  );
});

export default Sheet;
